import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/rootReducer';

export type SpinnerStatus = {
  isShowing: boolean;
  type?: 'default' | 'mobile';
};

export type ConsentStatus = {
  isAllowed: boolean;
};

export enum ToastType {
  Default,
  Success,
  Warning,
  Danger,
}

export type ToastData = {
  msg: string;
  title: string;
  id?: number;
  subTitle?: string;
  type?: ToastType;
  hidden?: boolean;
  delay?: number;
};

export type SystemStatus = {
  spinner: SpinnerStatus;
  consentDataStatus: ConsentStatus;
  toasts: ToastData[];
  transferStatus: string;
  transferPatientDetailInfo: {};
  // FKA org delimitation R3: this login's FKO validation degraded, so the
  // organisation set the user is working against may be stale. NOT an error
  // state — the login succeeded and the app is fully usable; it drives one
  // dismissible dialog.
  fkoDegraded: boolean;
};

let currentToastId = 1;

const slice = createSlice({
  name: 'system',
  initialState: {
    spinner: { isShowing: false },
    consentDataStatus: { isAllowed: false },
    toasts: [],
    transferStatus: '', // ehospitalet, pending, hospitalet, deceased
    transferPatientDetailInfo: {},
    fkoDegraded: false,
  } as SystemStatus,
  reducers: {
    setFkoDegraded: (state, { payload }: PayloadAction<boolean>) => {
      state.fkoDegraded = payload;
    },
    setTransferPatientDetailInfo: (state, { payload }: PayloadAction<{}>) => {
      state.transferPatientDetailInfo = payload;
    },
    setTransferStatus: (state, { payload }: PayloadAction<string>) => {
      state.transferStatus = payload;
    },
    setConsentDataStatus: (
      state,
      { payload }: PayloadAction<ConsentStatus>
    ) => {
      state.consentDataStatus.isAllowed = payload.isAllowed;
    },
    setSpinnerStatus: (state, { payload }: PayloadAction<SpinnerStatus>) => {
      state.spinner.isShowing = payload.isShowing;
      if (payload.type) {
        state.spinner.type = payload.type;
      } else {
        state.spinner.type = 'default';
      }
    },
    addToast: (state, { payload }: PayloadAction<ToastData>) => {
      payload.delay = payload.delay ?? 6000;
      payload.type = payload.type ?? ToastType.Default;
      state.toasts.push({ ...{ id: currentToastId++ }, ...payload });
    },
    refreshToasts: (state, { payload }: PayloadAction<ToastData[]>) => {
      state.toasts = [...payload];
    },
  },
});

export default slice.reducer;

export const selectConsentDataStatus = (state: RootState) =>
  state.system.consentDataStatus;
export const selectSpinnerStatus = (state: RootState) => state.system.spinner;
export const selectToasts = (state: RootState) => state.system.toasts;
export const selectTransferStatus = (state: RootState) =>
  state.system.transferStatus;
export const selectTransferPatientDetailInfo = (state: RootState) =>
  state.system.transferPatientDetailInfo;
export const selectFkoDegraded = (state: RootState) => state.system.fkoDegraded;

export const {
  setConsentDataStatus,
  setSpinnerStatus,
  addToast,
  refreshToasts,
  setTransferStatus,
  setTransferPatientDetailInfo,
  setFkoDegraded,
} = slice.actions;
